import { approximateDistance, formatNumberWithSpaces, getLibMeasurementSystem, getMeasurementSystem, haversineDistance } from '../utils/Utils';
import { Connection, ConnectionTypes, createConnectionFromJson } from './Connection';
import { Coordinates } from './Coordinates';
import { OpeningTimeDay } from './OpeningTimeDay';
import { WalletType } from './Transactions';
import { UserBadgeType } from './UserBadgeType';
import { UserType } from './UserType';

export enum WalletMigrationStatus {
  NOT_STARTED = 'not_started',
  READY_TO_MIGRATE = 'ready_to_migrate',
  MIGRATED = 'migrated',
}

export interface SlugChanged {
  previousSlug: string;
  changedAt: string;
}

export interface PlebData {
  id: string;
  name: string;
  slug: string;
  roles: string[];
  merchant: boolean;
  bio?: string;
  profession?: string;
  website?: string;
  twitterSlug?: string;
  connection?: Connection;
  pictures: string[];
  image: string;
  thumbnail: string;
  coordinates?: Coordinates;
  city?: string;
  email?: string;
  subscription?: string;
  specialBadge?: string;
  badges?: UserBadgeType[];
  nostr?: string;
  interests?: string;
  address?: string;
  phoneNumber?: string;
  zapAddress?: string;
  sparkStaticAddress?: string;
  walletType?: WalletType;
  walletMigrationStatus?: WalletMigrationStatus;
  hasFundsToMigrate?: boolean;
  openingTimes: Record<string, any>;
  hideDistance?: boolean;
  wallet?: {
    lnurl: string;
    balanceMsat?: number;
    platform?: string;
    appVersion?: string;
    deviceId?: string;
  };
  yob?: number;
  memberNumber?: number;
  shareLink?: string;
  houseRulesAccepted?: boolean;
  distance?: number;
  slugChanged?: SlugChanged | null;
}

export class Pleb {
  id: string;
  name: string;
  slug: string;
  roles: string[];
  merchant: boolean;
  bio?: string;
  profession?: string;
  website?: string;
  twitterSlug?: string;
  connection?: Connection;
  pictures: string[];
  image: string;
  thumbnail: string;
  coordinates?: Coordinates;
  city?: string;
  email?: string;
  subscription?: string;
  specialBadge?: string;
  badges?: UserBadgeType[];
  nostr?: string;
  interests?: string;
  address?: string;
  phoneNumber?: string;
  zapAddress?: string;
  sparkStaticAddress?: string;
  walletType?: WalletType;
  walletMigrationStatus?: WalletMigrationStatus;
  hasFundsToMigrate?: boolean;
  openingTimes: Record<string, OpeningTimeDay>;
  hideDistance?: boolean;
  wallet?: {
    lnurl: string;
    balanceMsat?: number;
    platform?: string;
    appVersion?: string;
    deviceId?: string;
  };
  yob?: number;
  memberNumber?: number;
  shareLink?: string;
  houseRulesAccepted?: boolean;
  distance?: number;
  slugChanged?: SlugChanged | null;

  constructor(data: PlebData) {
    this.id = data.id;
    this.name = data.name;
    this.slug = data.slug;
    this.roles = data.roles ?? [];
    this.merchant = data.merchant;
    this.bio = data.bio;
    this.profession = data.profession;
    this.website = data.website;
    this.twitterSlug = data.twitterSlug;
    this.connection = data.connection;
    this.pictures = data.pictures;
    this.image = data.image;
    this.thumbnail = data.thumbnail;
    this.coordinates = data.coordinates;
    this.city = data.city;
    this.email = data.email;
    this.subscription = data.subscription;
    this.specialBadge = data.specialBadge;
    this.badges = data.badges;
    this.nostr = data.nostr;
    this.interests = data.interests;
    this.address = data.address;
    this.phoneNumber = data.phoneNumber;
    this.zapAddress = data.zapAddress;
    this.sparkStaticAddress = data.sparkStaticAddress;
    this.walletType = data.walletType;
    this.walletMigrationStatus = data.walletMigrationStatus;
    this.hasFundsToMigrate = data.hasFundsToMigrate;
    this.openingTimes = data.openingTimes;
    this.hideDistance = data.hideDistance;
    this.wallet = data.wallet;
    this.yob = data.yob;
    this.memberNumber = data.memberNumber;
    this.shareLink = data.shareLink;
    this.houseRulesAccepted = data.houseRulesAccepted;
    this.distance = data.distance;
    this.slugChanged = data.slugChanged ?? null;
  }

  get formattedSlug(): string {
    return `@${this.slug}`;
  }

  get badge(): UserBadgeType | null {
    return this.badges?.length ? this.badges[0] : null;
  }

  get type(): UserType {
    return this.merchant ? UserType.merchant : UserType.pleb;
  }

  get subscriptionPeriod(): string | null {
    return this.subscription?.toLowerCase().includes('gigachad') ? 'Gigachad'
      : this.subscription?.toLowerCase().includes('lifetime') ? 'Lifetime'
      : this.subscription?.toLowerCase().includes('founder') ? 'Founder'
      : this.subscription?.toLowerCase().includes('yearly') ? 'Yearly'
      : this.subscription?.toLowerCase().includes('1year') ? 'Yearly'
      : this.subscription?.toLowerCase().includes('month') ? 'Monthly'
      : this.subscription?.toLowerCase().includes('plebs') ? 'Monthly'
      : null;
  }

  static createConnection(json: Record<string, any>, currentUserId: string): Connection {
    const userJson = json.user;
    const connectionJson = json.connection;

    let connection: Connection;
    if (connectionJson) {
      connection = createConnectionFromJson(connectionJson, {
        currentUserId,
        userId: userJson._id
      });
    } else if (currentUserId === userJson._id) {
      connection = new Connection({
        type: ConnectionTypes.MY_SELF
      });
    } else {
      connection = new Connection({
        type: ConnectionTypes.NOT_ESTABLISHED
      });
    }

    return connection
  }

  getLabelToShowSendToWallet(): string {
    /*if(this.zapAddress)
      return this.zapAddress;
    else*/
      return this.name;
  }

  hasWallet(): boolean {
    return this.sparkStaticAddress !== undefined || this.zapAddress !== undefined || this.wallet?.lnurl !== undefined;
  }

  shouldShowNameAndPicture(): boolean {
    /*if(this.zapAddress)
      return false;
    else*/
      return true;
  }

  static fromJson(json: Record<string, any>, currentUserId: string): Pleb {
    const userJson = json.user;
    const connection = Pleb.createConnection(json, currentUserId);

    const coordinatesList = userJson.location?.coordinates as number[] | undefined;
    let coordinates: Coordinates | undefined;
    if (coordinatesList?.length) {
      coordinates = {
        latitude: coordinatesList[1],
        longitude: coordinatesList[0]
      };
    }

    const openingTimesJson = userJson.openingTimes as Record<string, any> | undefined;
    const openingTimes = openingTimesJson
      ? Object.fromEntries(
          Object.entries(openingTimesJson).map(
            ([key, value]) => [key, OpeningTimeDay.fromJson(value)]
          )
        )
      : {};

    const slugChangedJson = json.slugChanged;
    const slugChanged: SlugChanged | null =
      slugChangedJson?.previousSlug && slugChangedJson?.changedAt
        ? {
            previousSlug: slugChangedJson.previousSlug,
            changedAt: slugChangedJson.changedAt,
          }
        : null;

    return new Pleb({
      id: userJson._id,
      name: userJson.name,
      slug: userJson.slug,
      roles: userJson.roles ?? [],
      merchant: userJson.merchant,
      bio: userJson.bio,
      profession: userJson.profession,
      website: userJson.website,
      twitterSlug: userJson.twitterSlug,
      connection,
      pictures: userJson.pictures.map((p: any) => p.url),
      image: userJson.publicPictureURL,
      thumbnail: userJson.publicThumbnailURL ?? userJson.publicPictureURL,
      coordinates,
      city: userJson.city,
      email: userJson.email,
      subscription: userJson.subscription?.id,
      specialBadge: userJson.specialBadge,
      badges: userJson.badges,
      nostr: userJson.nostrPublicKey,
      interests: userJson.interests,
      address: userJson.address,
      phoneNumber: userJson.phoneNumber,
      zapAddress: userJson.zapAddress,
      sparkStaticAddress: userJson.sparkStaticAddress,
      walletType: userJson.walletType,
      walletMigrationStatus: userJson.walletMigrationStatus,
      hasFundsToMigrate: userJson.hasFundsToMigrate,
      openingTimes,
      hideDistance: userJson.hideDistance,
      wallet: json.wallet ? {
        lnurl: json.wallet.lnurl,
        balanceMsat: json.wallet.balanceMsat ?? undefined,
        platform: json.wallet.platform ?? undefined,
        appVersion: json.wallet.appVersion ?? undefined,
        deviceId: json.wallet.deviceId ?? undefined,
      } : undefined,
      yob: userJson.yob,
      memberNumber: userJson.memberNumber,
      shareLink: userJson.shareLink,
      distance: userJson.distance,
      slugChanged,
    });
  }

  static currentUserFromJson(json: Record<string, any>): Pleb {
    const coordinatesList = json.location?.coordinates as number[] | undefined;
    let coordinates: Coordinates | undefined;
    if (coordinatesList?.length) {
      coordinates = {
        latitude: coordinatesList[1],
        longitude: coordinatesList[0]
      };
    }

    const openingTimesJson = json.openingTimes as Record<string, any> | undefined;
    const openingTimes = openingTimesJson
      ? Object.fromEntries(
          Object.entries(openingTimesJson).map(
            ([key, value]) => [key, OpeningTimeDay.fromJson(value)]
          )
        )
      : {};

    return new Pleb({
      id: json._id,
      name: json.name,
      slug: json.slug,
      roles: json.roles ?? [],
      merchant: json.merchant,
      bio: json.bio,
      profession: json.profession,
      website: json.website,
      twitterSlug: json.twitterSlug,
      connection: new Connection({
        type: ConnectionTypes.MY_SELF
      }),
      pictures: json.pictures.map((p: any) => p.url),
      image: json.publicPictureURL,
      thumbnail: json.publicThumbnailURL ?? json.publicPictureURL,
      coordinates,
      city: json.city,
      email: json.email,
      subscription: json.subscription?.id,
      specialBadge: json.specialBadge,
      badges: json.badges,
      nostr: json.nostrPublicKey,
      interests: json.interests,
      address: json.address,
      phoneNumber: json.phoneNumber,
      zapAddress: json.zapAddress,
      sparkStaticAddress: json.sparkStaticAddress,
      walletType: json.walletType,
      walletMigrationStatus: json.walletMigrationStatus,
      hasFundsToMigrate: json.hasFundsToMigrate,
      openingTimes,
      hideDistance: json.hideDistance,
      wallet: json.wallet,
      yob: json.yob,
      memberNumber: json.memberNumber,
      shareLink: json.shareLink,
      houseRulesAccepted: json.houseRulesAccepted,
      distance: json.distance
    });
  }

  toJson(): PlebData {
    return {
      id: this.id,
      name: this.name,
      slug: this.slug,
      roles: this.roles,
      merchant: this.merchant,
      bio: this.bio,
      profession: this.profession,
      website: this.website,
      twitterSlug: this.twitterSlug,
      pictures: this.pictures,
      image: this.image,
      thumbnail: this.thumbnail,
      coordinates: this.coordinates,
      city: this.city,
      email: this.email,
      subscription: this.subscription,
      specialBadge: this.specialBadge,
      badges: this.badges,
      nostr: this.nostr,
      interests: this.interests,
      address: this.address,
      phoneNumber: this.phoneNumber,
      zapAddress: this.zapAddress,
      sparkStaticAddress: this.sparkStaticAddress,
      walletType: this.walletType,
      walletMigrationStatus: this.walletMigrationStatus,
      hasFundsToMigrate: this.hasFundsToMigrate,
      openingTimes: {},
      hideDistance: this.hideDistance,
      wallet: this.wallet,
      yob: this.yob,
      memberNumber: this.memberNumber,
      shareLink: this.shareLink,
      houseRulesAccepted: this.houseRulesAccepted,
      distance: this.distance,
      slugChanged: this.slugChanged,
    };
  }
}

export function canManageMembers(pleb: Pleb | PlebData): boolean {
  return pleb.roles.includes('membersManager');
}

export function isAdmin(pleb: Pleb | PlebData): boolean {
  return pleb.roles.includes('admin');
}

export function formatDistance(distance: number) : string {
  return formatNumberWithSpaces(distance) + ' ' + getLibMeasurementSystem();
}

export function isSubscriptionLifetime(subscription: string): boolean {
  const normalizedSubscription = subscription?.toLowerCase() ?? '';
  return (
    normalizedSubscription.includes('lifetime') ||
    normalizedSubscription.includes('gigachad') || 
    normalizedSubscription.includes('founder') ||
    normalizedSubscription.includes('whale')
  );
}